import { forwardRef, useRef, useState } from 'react';

import { mergeRefs } from '@utils/mergeRefs';
import { BaseInput } from '../BaseInput';
import type { BaseInputProps } from '../BaseInput';

import { ToggleVisibilityAction } from '@components/Actions/ToggleVisibilityAction/ToggleVisibilityAction';
import { CopyAction } from '@components/Actions/CopyAction/CopyAction';

export interface PasswordInputProps
  extends Omit<
    BaseInputProps,
    'actionContent' | 'customContent' | 'showInvalidIcon' | 'type' | 'ref'
  > {
  hideToggleVisibility?: boolean;
}

/**
 * PasswordInput component
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/856f6e-text-input/b/87505a Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-passwordinput--basic-password-input Code documentation}
 *
 * @example
 * <PasswordInput defaultValue="My password" />
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      hideToggleVisibility,
      isReadonly,
      isDisabled,
      ...props
    }: PasswordInputProps,
    ref,
  ): JSX.Element => {
    const innerRef = useRef<HTMLInputElement>(null);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    return (
      <BaseInput
        {...props}
        isReadonly={isReadonly}
        isDisabled={isDisabled}
        type={isPasswordVisible || isReadonly ? 'text' : 'password'}
        ref={mergeRefs([innerRef, ref])}
        actionContent={
          isReadonly ? (
            <CopyAction
              toCopy={() => (innerRef ? innerRef.current.value : null)}
              description="Copy password"
            />
          ) : !hideToggleVisibility && !isDisabled ? (
            <ToggleVisibilityAction
              description={`${isPasswordVisible ? 'Hide' : 'Show'} password`}
              isVisible={isPasswordVisible}
              onVisibilityChange={(isVisible) =>
                setIsPasswordVisible(isVisible)
              }
            />
          ) : null
        }
      />
    );
  },
);

PasswordInput.displayName = 'PasswordInput';

PasswordInput.defaultProps = {
  isDisabled: false,
  isInvalid: false,
  isRequired: false,
  isReadonly: false,
  size: 'm',
  hideToggleVisibility: false,
};
